import { Link } from 'react-router-dom';
import { 
  Home, CreditCard, Bell, TrendingUp, Calendar, 
  MapPin, ArrowRight 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientDashboardStats, useMySouscriptions, useMyPaiements, useMyProjectUpdates } from '../../hooks/useClientApi';
import { useTexts } from '../../hooks/useTexts';
import Breadcrumb from '../../components/common/Breadcrumb';

export default function DashboardPage() {
  const { user } = useAuth();
  
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  
  // Utiliser les hooks pour récupérer les données réelles
  const { data: stats, loading: statsLoading, error: statsError } = useClientDashboardStats();
  const { data: souscriptionsData, loading: souscriptionsLoading } = useMySouscriptions({ limit: 5 });
  const { data: paiementsData, loading: paiementsLoading } = useMyPaiements({ limit: 5 });
  const { data: projectUpdatesData, loading: projectUpdatesLoading } = useMyProjectUpdates();

  // Fonction de formatage des montants
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  };
  
  const userSubscriptions = souscriptionsData?.results || [];
  const recentPayments = (paiementsData?.results || []).slice(0, 5); // Limiter à 5 paiements
  const recentUpdates = (projectUpdatesData || []).slice(0, 5); // Limiter à 5 actualités
  
  // Calculer les statistiques à partir des données réelles
  const totalSubscriptions = stats?.total_souscriptions || 0;
  const totalPaid = stats?.total_revenus || 0;
  const totalRemaining = stats?.montant_restant_total || 0;

  const dashboardStats = [
    {
      icon: Home,
      label: getText('dashboard.my_subscriptions'),
      value: totalSubscriptions,
      color: 'blue',
      link: '/subscriptions'
    },
    {
      icon: CreditCard,
      label: getText('dashboard.amount_paid'),
      value: formatCurrency(totalPaid),
      color: 'green',
      link: '/payments'
    },
    {
      icon: TrendingUp,
      label: getText('dashboard.remaining_amount'),
      value: formatCurrency(totalRemaining),
      color: 'orange',
      link: '/subscriptions'
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  // Affichage de l'état de chargement
  if (statsLoading || souscriptionsLoading || paiementsLoading || projectUpdatesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (statsError) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">Erreur de chargement</h3>
            <p className="text-red-600">{statsError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('breadcrumb.dashboard'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {getText('dashboard.welcome')} {user?.prenoms} {user?.nom}
          </h1>
          <p className="text-gray-600 mt-2">
            {getText('dashboard.description')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {dashboardStats.map((stat, index) => (
            <Link
              key={index}
              to={stat.link}
              className={`p-6 rounded-xl border-2 hover:shadow-lg transition-all duration-200 ${getColorClasses(stat.color)}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium opacity-75 mb-2">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-bold">
                    {stat.value}
                  </p>
                </div>
                <stat.icon className="w-8 h-8 opacity-75" />
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Souscriptions actives */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {getText('dashboard.active_subscriptions')}
                  </h2>
                  <Link
                    to="/subscriptions"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                  >
                    {getText('dashboard.see_all')}
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {userSubscriptions.length > 0 ? (
                  <div className="space-y-4">
                    {userSubscriptions.map((subscription) => (
                      <div key={subscription.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {subscription.projet_nom || 'Projet non défini'}
                            </h3>
                            <div className="flex items-center text-sm text-gray-600 mb-2">
                              <MapPin className="w-4 h-4 mr-1" />
                              {subscription.batiment_nom || 'Bâtiment non défini'}
                            </div>
                            <p className="text-sm text-gray-600">
                              Logement: {subscription.type_maison_designation || 'Type non défini'}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            subscription.statut_souscription === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                            subscription.statut_souscription === 'SUSPENDUE' ? 'bg-orange-100 text-orange-800' :
                            subscription.statut_souscription === 'TERMINEE' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {subscription.statut_souscription}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className='text-xs text-gray-500'>{getText('table.price')}</p>
                            <p className="font-semibold text-gray-900">
                              {subscription.prix_convenu_ht ? formatCurrency(subscription.prix_convenu_ht) : formatCurrency(0)}
                            </p>
                          </div>
                          <div>
                            <p className='text-xs text-gray-500'>{getText('table.remaining')}</p>
                            <p className="font-semibold text-gray-900">
                              {subscription.montant_restant ? formatCurrency(subscription.montant_restant) : formatCurrency(0)}
                            </p>
                          </div>
                        </div>

                        {subscription.prix_convenu_ht && subscription.montant_restant && (
                          <div className="mb-3">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>{getText('project.progress')}</span>
                              <span>{Math.round(((subscription.prix_convenu_ht - subscription.montant_restant) / subscription.prix_convenu_ht) * 100)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${Math.round(((subscription.prix_convenu_ht - subscription.montant_restant) / subscription.prix_convenu_ht) * 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-1" />
                            Souscription: {new Date(subscription.date_souscription).toLocaleDateString('fr-FR')}
                          </div>
                          <Link
                            to={`/subscriptions/${subscription.id}`}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {getText('project.view_details')}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Home className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {getText('dashboard.no_subscriptions')}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {getText('dashboard.no_subscriptions_desc')}
                    </p>
                    <Link
                      to="/projects"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {getText('dashboard.discover_projects')}
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Derniers paiements */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {getText('dashboard.last_payments')}
                  </h2>
                  <Link
                    to="/payments"
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                  >
                    {getText('dashboard.see_all')}
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="p-6">
                {recentPayments.length > 0 ? (
                  <div className="space-y-4">
                    {recentPayments.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-3 ${
                            payment.statut_paiement === 'VALIDE' ? 'bg-green-500' :
                            payment.statut_paiement === 'EN_ATTENTE' ? 'bg-orange-500' :
                            'bg-red-500'
                          }`} />
                          <div>
                            <p className="font-medium text-gray-900">
                              {payment.montant_total_paye ? formatCurrency(Number(payment.montant_total_paye)) : formatCurrency(Number(payment.montant_paye || 0))}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(payment.date_paiement).toLocaleDateString('fr-FR')} - {payment.mode_paiement}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            payment.statut_paiement === 'VALIDE' ? 'bg-green-100 text-green-800' :
                            payment.statut_paiement === 'EN_ATTENTE' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {payment.statut_paiement}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className='text-gray-600'>{getText('dashboard.no_payments')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actualités de vos projets */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {getText('dashboard.project_news')}
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentUpdates.length > 0 ? (
                    recentUpdates.map((update) => (
                      <div key={update.id} className="border-l-4 border-blue-500 pl-4 pb-4 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">
                            {new Date(update.date_publication).toLocaleDateString('fr-FR')}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            update.type_mise_a_jour === 'AVANCEMENT' ? 'bg-green-100 text-green-800' :
                            update.type_mise_a_jour === 'LIVRAISON' ? 'bg-blue-100 text-blue-800' :
                            update.type_mise_a_jour === 'RETARD' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {update.type_mise_a_jour}
                          </span>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">
                          {update.titre}
                        </h4>
                        <p className="text-sm text-gray-600 mb-2">
                          {update.description.substring(0, 100)}...
                        </p>
                        {/* Images associées à l'actualité */}
                        {update.medias && update.medias.length > 0 && (
                          <div className="mt-3 mb-2">
                            <div className="flex gap-2 overflow-x-auto pb-2">
                              {update.medias.slice(0, 3).map((media: any, idx: number) => (
                                <img
                                  key={media.id || idx}
                                  src={media.url}
                                  alt={media.description || media.nom_fichier || `Image ${idx + 1}`}
                                  className="w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ))}
                              {update.medias.length > 3 && (
                                <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-xs text-gray-500 flex-shrink-0">
                                  +{update.medias.length - 3}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {update.pourcentage_avancement && (
                          <p className="text-xs text-blue-600 font-medium">
                            {update.pourcentage_avancement}% terminé
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <Bell className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className='text-sm text-gray-600'>{getText('dashboard.no_news')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Statistiques rapides de vos projets */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {getText('dashboard.quick_stats')}
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className='text-sm text-gray-600'>{getText('stats.paid_installments')}</span>
                    <span className="font-semibold text-green-600">{stats?.echeances_payees || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className='text-sm text-gray-600'>{getText('stats.pending_installments')}</span>
                    <span className="font-semibold text-orange-600">{stats?.echeances_en_attente || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className='text-sm text-gray-600'>{getText('stats.overdue_installments')}</span>
                    <span className="font-semibold text-red-600">{stats?.echeances_en_retard || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className='text-sm text-gray-600'>{getText('stats.collection_rate')}</span>
                    <span className="font-semibold text-blue-600">{stats?.taux_recouvrement || 0}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}